import { defineQuery } from "@nova/queryable"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { Card } from "features/pilot-tracking/MetricLayout"
import { useMemo, useState } from "react"
import * as Icons from "@nova/component-icons"
import { IconButton } from "@nova/component-btn"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { pluralize } from "@nova/util-strings"
import { useAppDispatch } from "integrations/redux/hooks"
import { setDateFilter } from "features/pilot-tracking/redux/missions-table-slice"
import { setTab } from "features/pilot-tracking/aircraft/slice"

const DAY_HEADERS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]

// Monday-first offset: Sun=6, Mon=0, Tue=1, ...
const mondayFirstOffset = (date: Date) => (date.getDay() === 0 ? 6 : date.getDay() - 1)
const today = new Date()
const toISODate = (d: Date) => {
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const AircraftDailyUseCalendar = ({ aircraftId }: { aircraftId: string }) => {
    const dispatch = useAppDispatch()

    const [monthStart, setMonthStart] = useState(
        () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    )

    const isCurrentMonth =
        monthStart.getFullYear() === today.getFullYear() &&
        monthStart.getMonth() === today.getMonth()

    const prevMonth = () => setMonthStart((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
    const nextMonth = () => setMonthStart((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))

    const queries = useMemo(() => {
        const end = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1)
        return {
            flightDays: aircraftFlightDaysQuery(aircraftId, monthStart, end),
            oldestFlight: aircraftOldestFlightQuery(aircraftId),
        }
    }, [aircraftId, monthStart])

    const { data: batchData } = useBatchPilotTrackingQuery(queries)
    const data = batchData?.flightDays

    const oldestMonthStart = useMemo(() => {
        const raw = batchData?.oldestFlight?.[0]?.oldestFlight
        if (!raw) return null
        const d = new Date(raw)
        return new Date(d.getFullYear(), d.getMonth(), 1)
    }, [batchData])

    const isEarliestMonth = oldestMonthStart === null || monthStart <= oldestMonthStart

    const flightsByDay = useMemo(() => {
        const map = new Map<string, number>()
        data?.forEach((row) => {
            if (!row.flightDay) return
            const key = new Date(row.flightDay).toISOString().split("T")[0]
            map.set(key, Number(row.numFlights ?? 0))
        })
        return map
    }, [data])

    const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate()
    const startOffset = mondayFirstOffset(monthStart)
    const totalCells = startOffset + daysInMonth
    const rows = Math.ceil(totalCells / 7)

    const daysInUse = flightsByDay.size

    const handleDayClick = (day: Date) => {
        const dateStr = toISODate(day)
        dispatch(setDateFilter({ label: dateStr, from: dateStr, to: dateStr }))
        dispatch(setTab("history"))
    }

    return (
        <Card className="flex flex-col gap-4 w-fit p-8 bg-white-100">
            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                    <h5 className="text-gray-400">{monthStart.getFullYear()}</h5>
                    {!isCurrentMonth && (
                        <IconButton
                            onClick={() =>
                                setMonthStart(new Date(today.getFullYear(), today.getMonth(), 1))
                            }
                        >
                            Today
                        </IconButton>
                    )}
                </div>
                <div className="flex items-center justify-between">
                    <h2 className="text-white">
                        {monthStart.toLocaleDateString("en-US", { month: "long" })}
                    </h2>
                    <div className="flex items-center gap-1 text-gray-400">
                        <IconButton onClick={prevMonth} disabled={isEarliestMonth}>
                            <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                        </IconButton>
                        <IconButton onClick={nextMonth} disabled={isCurrentMonth}>
                            <Icons.SmallIcon Icon={Icons.ArrowRight} />
                        </IconButton>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-1">
                {DAY_HEADERS.map((d) => (
                    <div key={d} className="text-center text-xs text-gray-300 py-1 w-12 pb-3">
                        {d}
                    </div>
                ))}
                {Array.from({ length: rows * 7 }).map((_, i) => {
                    const dayNum = i - startOffset + 1
                    if (dayNum < 1 || dayNum > daysInMonth) {
                        return <div key={i} className="w-12 h-7" />
                    }
                    const dateKey = new Date(
                        Date.UTC(monthStart.getFullYear(), monthStart.getMonth(), dayNum)
                    )
                        .toISOString()
                        .split("T")[0]
                    const numFlights = flightsByDay.get(dateKey)
                    const isActive = numFlights !== undefined
                    const Component = isActive ? "button" : "div"

                    const day = new Date(monthStart.getFullYear(), monthStart.getMonth(), dayNum)
                    return (
                        <Component
                            key={i}
                            className="relative group/tooltip"
                            onClick={isActive ? () => handleDayClick(day) : undefined}
                        >
                            <h5
                                className={`w-12 py-[7px] flex items-center justify-center rounded-lg text-sm ${
                                    isActive
                                        ? "bg-green-200 text-gray-900 opacity-70 hover:opacity-80"
                                        : "text-gray-300"
                                }`}
                            >
                                {dayNum}
                            </h5>
                            {isActive && (
                                <Tooltip
                                    title={`${pluralize({ count: numFlights, str: "flight" })}`}
                                    placement="right"
                                />
                            )}
                        </Component>
                    )
                })}
            </div>

            <div className="grid grid-cols-[auto_1fr] grid-rows-2 w-fit gap-x-2 items-center">
                <span className="w-2 h-2 rounded-full bg-green-200" />
                <small className="text-gray-400">Days in use</small>
                <h2 className="text-gray-0 col-start-2">{daysInUse}</h2>
            </div>
        </Card>
    )
}

const aircraftFlightDaysQuery = defineQuery(
    (aircraftId: string, monthStart: Date, monthEnd: Date) => ({
        resource: "metadata",
        timeBuckets: [{ field: "started_at", unit: "day", alias: "flightDay" }],
        aggregates: [{ field: "mission_id", func: "COUNT", alias: "numFlights" }],
        filters: [
            { field: "mission.aircraft_id", op: "=", value: aircraftId },
            { field: "started_at", op: ">=", value: monthStart.toISOString() },
            { field: "started_at", op: "<", value: monthEnd.toISOString() },
        ],
    })
)

const aircraftOldestFlightQuery = defineQuery((aircraftId: string) => ({
    resource: "metadata",
    aggregates: [{ field: "started_at", func: "MIN", alias: "oldestFlight" }],
    filters: [{ field: "mission.aircraft_id", op: "=", value: aircraftId }],
}))

export { AircraftDailyUseCalendar }
